import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Tag, Collapse, Empty, Skeleton, Typography, Button, Drawer } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { useSearchQuery, useGetFacetsQuery } from './searchApi';
import DealCard from '../../../../../packages/ui/src/DealCard';
import SearchInput from '../../../../../packages/ui/src/SearchInput';

const { Text } = Typography;
const { Panel } = Collapse;

function FacetPanel({ facets }) {
  if (!facets) return null;

  return (
    <div
      className="df-surface-panel"
      style={{
        borderRadius: 12,
        border: '1px solid var(--df-border)',
        background: 'var(--df-surface-elevated)',
        boxShadow: 'var(--df-shadow)',
        padding: 18,
      }}
    >
      <Text
        strong
        style={{
          display: 'block',
          marginBottom: 12,
          fontFamily: 'var(--df-font-display)',
          color: 'var(--df-text)',
          fontSize: 14,
        }}
      >
        Refine results
      </Text>
      <Collapse defaultActiveKey={['categories', 'merchants', 'price']} ghost>
        {facets?.categories?.length > 0 && (
          <Panel
            header={<Text strong style={{ fontSize: 13, color: 'var(--df-text)' }}>Categories</Text>}
            key="categories"
          >
            {facets.categories.map((c) => (
              <div key={c.slug} style={{ padding: '6px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Text style={{ fontSize: 13, color: 'var(--df-text-secondary)' }}>{c.name}</Text>
                <Tag style={{ borderRadius: 6, border: 'none', background: 'var(--df-border-subtle)', color: 'var(--df-text-secondary)', margin: 0 }}>
                  {c.count}
                </Tag>
              </div>
            ))}
          </Panel>
        )}
        {facets?.merchants?.length > 0 && (
          <Panel
            header={<Text strong style={{ fontSize: 13, color: 'var(--df-text)' }}>Merchants</Text>}
            key="merchants"
          >
            {facets.merchants.map((m) => (
              <div key={m.slug} style={{ padding: '6px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Text style={{ fontSize: 13, color: 'var(--df-text-secondary)' }}>{m.name}</Text>
                <Tag style={{ borderRadius: 6, border: 'none', background: 'var(--df-border-subtle)', color: 'var(--df-text-secondary)', margin: 0 }}>
                  {m.count}
                </Tag>
              </div>
            ))}
          </Panel>
        )}
        {facets?.priceRanges && (
          <Panel
            header={<Text strong style={{ fontSize: 13, color: 'var(--df-text)' }}>Price range</Text>}
            key="price"
          >
            {[
              { label: 'Under $25', count: facets.priceRanges.under25 },
              { label: '$25–$100', count: facets.priceRanges['25to100'] },
              { label: 'Over $100', count: facets.priceRanges.over100 },
            ].map((row) => (
              <div key={row.label} style={{ padding: '6px 0', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Text style={{ fontSize: 13, color: 'var(--df-text-secondary)' }}>{row.label}</Text>
                <Tag style={{ borderRadius: 6, border: 'none', background: 'var(--df-border-subtle)', color: 'var(--df-text-secondary)', margin: 0 }}>
                  {row.count}
                </Tag>
              </div>
            ))}
          </Panel>
        )}
      </Collapse>
    </div>
  );
}

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [searchText, setSearchText] = useState(q);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setSearchText(q);
  }, [q]);

  const { data: results, isLoading } = useSearchQuery({ q, limit: 30 }, { skip: !q });
  const { data: facets } = useGetFacetsQuery({ q }, { skip: !q });

  const handleSearch = (value) => {
    const next = (value ?? searchText).trim();
    if (next) setSearchParams({ q: next });
  };

  return (
    <>
      <section className="df-discovery-hero" aria-labelledby="search-heading">
        <div className="df-discovery-hero__brand df-brand df-brand--dark" style={{ animation: 'none' }}>
          <span className="df-brand__mark" aria-hidden="true">DF</span>
          <span className="df-brand__wordmark">DealFusion</span>
        </div>
        <h1 id="search-heading">Browse sharper prices</h1>
        <p>Search products, brands, and merchants — then refine with category and price facets.</p>
      </section>

      <div style={{ marginBottom: 24, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <SearchInput
          placeholder="Search deals, products, brands…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          style={{ flex: 1, minWidth: 220, maxWidth: 560 }}
          aria-label="Search deals"
        />
        {q && (
          <Button
            className="df-search-filter-btn"
            icon={<FilterOutlined />}
            onClick={() => setFiltersOpen(true)}
            style={{ height: 44, borderRadius: 8, fontWeight: 600, display: 'none' }}
            aria-label="Open filters"
          >
            Filters
          </Button>
        )}
      </div>

      {!q ? (
        <div
          style={{
            textAlign: 'center',
            padding: '64px 24px',
            borderRadius: 12,
            border: '1px dashed var(--df-border)',
            background: 'rgba(255,255,255,0.55)',
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text style={{ fontSize: 15, display: 'block', marginBottom: 8, color: 'var(--df-text)' }}>
                  Enter a search term to find deals
                </Text>
                <Text style={{ fontSize: 13, color: 'var(--df-text-secondary)' }}>
                  Try “headphones”, “smart watch”, or “4K TV”
                </Text>
              </div>
            }
          />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }} className="df-search-layout">
          <aside style={{ width: 260, flexShrink: 0 }} className="df-search-facets">
            <FacetPanel facets={facets} />
          </aside>

          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ display: 'block', marginBottom: 16, color: 'var(--df-text-secondary)', fontSize: 14 }}>
              {results?.items?.length || 0} results for “{q}”
            </Text>

            {isLoading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : results?.items?.length > 0 ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 20,
                }}
              >
                {results.items.map((deal) => (
                  <div
                    key={deal.publicId}
                    className="df-feed-item"
                    onClick={() => navigate(`/deals/${deal.publicId}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/deals/${deal.publicId}`); }}
                    role="link"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                  >
                    <DealCard deal={deal} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '64px 0', textAlign: 'center' }}>
                <Empty description="No deals match that search" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            )}
          </div>
        </div>
      )}

      <Drawer
        title="Filters"
        placement="bottom"
        height="78%"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        styles={{ body: { paddingTop: 8 } }}
      >
        <FacetPanel facets={facets} />
      </Drawer>

      <style>{`
        @media (max-width: 900px) {
          .df-search-facets { display: none; }
          .df-search-filter-btn { display: inline-flex !important; }
        }
      `}</style>
    </>
  );
}
